// Aerobic capacity: ACSM cycle-ergometer submaximal test as scored by ePhysioLogic.
// Spec: NGF-formulas-verified-v2.md § Aerobic capacity.
import { floor, round } from './num.ts';
import type { CycleStage, FitnessClass, ProvisionalRule, Sex } from './types.ts';

/** Metres travelled per pedal revolution on the Monark ergometer. */
const METRES_PER_REV = 6;

/** Age-predicted maximum heart rate as stored and printed (integer). */
export const maxHr = (age: number): number => floor(220 - age);

/**
 * VO2max (ml/kg/min, unrounded). Heart rate is regressed on work rate over all stages, extrapolated to
 * 220 − decimal age, and converted with the old ACSM leg equation (2.0 ml per kgm, +3.5 resting).
 * Weight converts with lb / 2.2 exactly as the legacy program did. Returns null when the stages cannot
 * support a regression (fewer than two distinct work rates, or HR not rising with work).
 */
export function vo2maxCycle(age: number, weightLb: number, stages: readonly CycleStage[]): number | null {
  const pts = stages.filter((s) => s.kp > 0 && s.rpm > 0 && s.hr > 0);
  if (pts.length < 2 || weightLb <= 0) return null;
  const w = pts.map((s) => s.kp * METRES_PER_REV * s.rpm);
  const hr = pts.map((s) => s.hr);
  const n = pts.length;
  const mw = w.reduce((a, b) => a + b, 0) / n;
  const mh = hr.reduce((a, b) => a + b, 0) / n;
  let sxy = 0, sxx = 0;
  for (let i = 0; i < n; i++) {
    sxy += (w[i]! - mw) * (hr[i]! - mh);
    sxx += (w[i]! - mw) ** 2;
  }
  if (sxx === 0) return null;
  const slope = sxy / sxx;
  if (slope <= 0) return null;
  const intercept = mh - slope * mw;
  const wMax = (220 - age - intercept) / slope;
  return (2.0 * wMax) / (weightLb / 2.2) + 3.5;
}

/** "Average for your age" VO2max (unrounded). */
export const ageNorm = (sex: Sex, age: number): number =>
  sex === 'M' ? 57.0 - 0.445 * age : 42.3 - 0.356 * age;

/** Age norm as printed: the legacy program kept it at 2 decimals, then displayed 1 (x.x45 → x.x5 → up). */
export const ageNormDisplayed = (sex: Sex, age: number): number => round(round(ageNorm(sex, age), 2), 1);

/** Percent of the age norm (the report prints 100 − this as "% below/above average"). */
export const pctOfAverage = (vo2max: number, sex: Sex, age: number): number =>
  floor((vo2max / ageNorm(sex, age)) * 100);

/** Lower VO2max bound for Fair / Average / Good / Excellent, by age band (integer age). */
type Thresholds = readonly [fair: number, average: number, good: number, excellent: number];
const BANDS = [30, 40, 50, 60] as const; // < 30, 30–39, 40–49, 50–59, 60+

const MALE: readonly Thresholds[] = [
  [24.6, 33.5, 42.6, 52.6],
  [22.6, 30.6, 38.6, 48.6],
  [19.5, 26.6, 35.5, 44.6],
  [17.5, 24.6, 33.5, 42.6],
  [15.5, 22.6, 30.6, 40.6],
];
// Derived 2026-09-22 from 10,755 archived female classifications (98.3–100 % per band). Where the data
// has a gap between two thresholds, the all-years fit is used.
const FEMALE: readonly Thresholds[] = [
  [23.5, 30.6, 37.5, 48.6],
  [19.5, 27.5, 33.5, 44.6],
  [16.6, 23.5, 30.6, 41.5],
  [14.6, 20.6, 27.5, 37.4],
  [12.5, 17.5, 23.5, 34.6],
];

export function aerobicClass(vo2max: number, sex: Sex, age: number): FitnessClass {
  const band = BANDS.filter((b) => Math.trunc(age) >= b).length;
  const t = (sex === 'M' ? MALE : FEMALE)[band]!;
  if (vo2max >= t[3]) return 'Excellent';
  if (vo2max >= t[2]) return 'Good';
  if (vo2max >= t[1]) return 'Average';
  if (vo2max >= t[0]) return 'Fair';
  return 'Poor';
}

export type ThrMethod = 'karvonen' | 'pctMax';

/**
 * Training (target) heart rate and its ±10 range. Karvonen is NGF's method (99.9 % of archived rows).
 * The legacy "% Max HR" option is floor(pct × max HR); archived rows only match it at 70 % about 70 %
 * of the time whatever percentage was stored, so it is supported but flagged provisional.
 */
export function targetHr(age: number, restHr: number | null, pct = 70, method: ThrMethod = 'karvonen') {
  const hrMax = maxHr(age);
  const thr =
    method === 'karvonen' && restHr !== null
      ? floor(restHr + (pct / 100) * (hrMax - restHr))
      : floor((pct / 100) * hrMax);
  return { thr, low: thr - 10, high: thr + 10 };
}

/** Exercise prescription intensity in METs, from the displayed (0.1-rounded) VO2max. */
export const prescriptionMets = (vo2maxDisplayed: number, pct = 70): number =>
  round(((pct / 100) * vo2maxDisplayed) / 3.5);

/**
 * "Achievable" VO2max printed on the aerobic page: displayed VO2max × a multiplier that shrinks as
 * fitness rises. Multipliers 1.40/1.35/1.30/1.20/1.05 were observed on packets; the 1.25/1.15/1.10
 * bands and the exact edges are inferred. PROVISIONAL.
 */
export function achievableVo2(vo2maxDisplayed: number, pctAvg: number): number {
  const vsAvg = pctAvg - 100;
  const f =
    vsAvg < -30 ? 1.4 : vsAvg < -20 ? 1.35 : vsAvg < -10 ? 1.3 : vsAvg < 0 ? 1.25
    : vsAvg < 10 ? 1.2 : vsAvg < 20 ? 1.15 : vsAvg < 30 ? 1.1 : 1.05;
  return round(vo2maxDisplayed * f, 2);
}

export interface AerobicInput {
  sex: Sex;
  age: number;
  weightLb: number;
  restHr: number | null;
  stages: readonly CycleStage[];
  thrPct?: number;
  thrMethod?: ThrMethod;
}

export interface AerobicResult {
  vo2max: number; // unrounded
  vo2maxDisplayed: number; // 0.1
  maxHr: number;
  ageNorm: number; // 0.1
  pctOfAverage: number;
  aerobicClass: FitnessClass;
  targetHr: { thr: number; low: number; high: number };
  mets: number;
  achievableVo2: number;
  provisional: ProvisionalRule[];
}

export function scoreAerobic(x: AerobicInput): AerobicResult | null {
  const vo2 = vo2maxCycle(x.age, x.weightLb, x.stages);
  if (vo2 === null) return null;
  const pct = x.thrPct ?? 70;
  const displayed = round(vo2, 1);
  const pctAvg = pctOfAverage(vo2, x.sex, x.age);
  const provisional: ProvisionalRule[] = ['achievable-vo2-bands'];
  if ((x.thrMethod ?? 'karvonen') === 'pctMax') provisional.push('target-hr-pct-max');
  return {
    vo2max: vo2,
    vo2maxDisplayed: displayed,
    maxHr: maxHr(x.age),
    ageNorm: ageNormDisplayed(x.sex, x.age),
    pctOfAverage: pctAvg,
    aerobicClass: aerobicClass(displayed, x.sex, x.age),
    targetHr: targetHr(x.age, x.restHr, pct, x.thrMethod ?? 'karvonen'),
    mets: prescriptionMets(displayed, pct),
    achievableVo2: achievableVo2(displayed, pctAvg),
    provisional,
  };
}
